<script setup lang="ts">
import debounce from 'lodash/debounce';
import { ApexOptions } from 'apexcharts';
import { ref, onMounted, watch, computed, toRaw, nextTick } from 'vue';
import { DashboardWidget, Serie, ApexChartType } from '@/interfaces/Dashboard';
import ConnxSkeletonLineGraph from './ConnxSkeletonLineGraph.vue';
import ConnxDashboardWidgetNoData from './ConnxDashboardWidgetNoData.vue';
import ConnxSkeletonDonutGraph from './ConnxSkeletonDonutGraph.vue';
import useDashboardStore from '@/stores/Dashboard/dashboard-store';

const props = defineProps<{ height: number; width: number; widget: DashboardWidget; options?: ApexOptions }>();
const emits = defineEmits(['data-updated']);
const dashboardStore = useDashboardStore();

// --- state
const data = ref<any>(null);
const status = ref<'idle' | 'loading' | 'formatting' | 'ready' | 'error'>('idle');
const chart = ref();
const renderIncrementer = ref(1);
const seriesData = ref<{ labels: string[] | null; series: any[] }>({ labels: null, series: [] });
const type = ref<ApexChartType>();
const rawType = ref<string>();

// Create a deep copy function for chart options
const createDefaultChartOptions = (): ApexOptions => ({
  chart: {
    type: 'bar',
    stacked: false,
    stackType: 'normal',
  },
  plotOptions: {
    bar: {
      horizontal: false,
      borderRadiusApplication: 'end',
      borderRadiusWhenStacked: 'last',
      borderRadius: 5,
      columnWidth: 20,
      barHeight: '100%',
      dataLabels: { position: 'center' },
    },
  },
  dataLabels: {
    enabled: false,
  },
  stroke: {
    show: true,
    width: 2,
    colors: ['transparent'],
  },
  xaxis: {
    categories: [],
  },
  yaxis: {
    title: {
      text: '',
    },
  },
  fill: {
    opacity: 1,
  },
  tooltip: {
    y: {
      formatter: function (val) {
        return '' + val;
      },
    },
  },
  legend: {
    show: true,
    showForSingleSeries: true,
  },
});

const chartOptions = ref<ApexOptions>(createDefaultChartOptions());

// --- derived state
const hasData = computed(() => seriesData.value.series.length > 0);

const shouldRenderChart = computed(() => status.value === 'ready' && hasData.value && !!chartOptions.value.chart?.type);

const renderKey = computed(() => `${props.widget.dashboardTabWidgetGuid}-${rawType.value}-${renderIncrementer.value}`);

const labelStyles = {
  fontSize: '13px',
  fontWeight: 'bold',
  colors: 'var(--connx-grey-shade-20)',
};

const percentageFormatter = (val: any) => {
  if (typeof val !== 'number' || isNaN(val)) return val;
  if (val === 0) return '';
  return `${val.toFixed(1)}%`;
};

const currencyFormatter = (val: number) =>
  new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(val);

const countFormatter = (val: any) => (val === 0 ? '' : val);

const chartHeight = computed(() => {
  const STANDARD_PADDING = 18;
  if (props.widget.defaultJsonProperties.currentDisplayType?.toLowerCase() === 'bar' && data.value) {
    const seriesInput = data.value.data ?? data.value.series ?? [];
    const barHeight = 40;
    const calculatedHeight = seriesInput.length * barHeight;
    return calculatedHeight < props.height ? props.height - STANDARD_PADDING : calculatedHeight;
  }
  if (props.widget.defaultJsonProperties.currentDisplayType?.toLowerCase() === 'column' && data.value) {
    if (typeof chartWidth.value === 'number' && chartWidth.value > props.width) {
      return props.height - STANDARD_PADDING;
    }
  }
  return '100%';
});

const chartWidth = computed<number | string>(() => {
  // number of categories (headings). fallback to length of first series data.
  const categoryCount = data.value?.data?.headings?.length ?? 0;

  // read the series input (same function you already have)
  const seriesInput = getSeriesInput();
  var seriesCount = Math.max(1, seriesInput?.length ?? 1);

  // If stacked, then only 1 column in each heading
  const currentDisplayTypeValue = props.widget.defaultJsonProperties.currentDisplayType;
  if (
    currentDisplayTypeValue === 'advancedcolumnchartstacked' ||
    currentDisplayTypeValue === 'advancedcolumnchartstacked100'
  ) {
    seriesCount = 1;
  }

  // tweak these values to taste:
  const BAR_WIDTH = 20; // px per single bar (what you asked for)
  const SERIES_GAP = 8; // px gap between bars inside a single category
  const CATEGORY_PADDING = 10; // px extra padding added per category (gap between groups)
  const H_PADDING = 10; // left + right chart padding (total 2 * this)

  if (!categoryCount) return '100%';

  // total width used by bars inside a category
  const barsRegion = seriesCount * BAR_WIDTH + Math.max(0, seriesCount - 1) * SERIES_GAP;

  // per-category slot (bars + gaps + category padding)
  const perCategorySlot = barsRegion + CATEGORY_PADDING;

  // total chart width (all categories + left/right padding)
  const totalWidth = perCategorySlot * categoryCount + H_PADDING * 2;

  // only return a large pixel width if it would overflow the available props.width --
  // otherwise let apex auto-fit with '100%'.
  return totalWidth > props.width ? Math.round(totalWidth) : '100%';
});

const skeletonMap = new Map([
  ['default', ConnxSkeletonLineGraph],
  ['line', ConnxSkeletonLineGraph],
  ['donut', ConnxSkeletonDonutGraph],
]);

const reRender = () => {
  renderIncrementer.value += 1;
};

const getSeriesInput = () => {
  const raw = data.value?.data;
  const series: Serie[] = Array.isArray(raw) ? raw : raw?.series ?? [];
  return series;
};

// --- formatData (only affects baseOptions)
const formatData = async () => {
  status.value = 'formatting';

  const currentDisplayTypeValue = props.widget.defaultJsonProperties.currentDisplayType;
  rawType.value = currentDisplayTypeValue;

  try {
    // Create fresh chart options
    const freshOptions = createDefaultChartOptions();

    // Apply widget-specific options if they exist
    if (props.widget.defaultJsonProperties.chartOptions) {
      Object.assign(freshOptions, props.widget.defaultJsonProperties.chartOptions);
    }

    const seriesInput = getSeriesInput();
    seriesData.value = { labels: null, series: [] };

    if (seriesInput.length === 0) {
      status.value = 'ready';
      return;
    }

    seriesData.value.series = seriesInput;

    // Set chart type and stacking based on currentDisplayType
    if (currentDisplayTypeValue === 'advancedcolumnchart') {
      freshOptions.chart = {
        ...freshOptions.chart,
        type: 'bar',
        stacked: false,
        stackType: 'normal',
      };
    } else if (currentDisplayTypeValue === 'advancedcolumnchartstacked') {
      freshOptions.chart = {
        ...freshOptions.chart,
        type: 'bar',
        stacked: true,
        stackType: 'normal',
      };
    } else if (currentDisplayTypeValue === 'advancedcolumnchartstacked100') {
      freshOptions.chart = {
        ...freshOptions.chart,
        type: 'bar',
        stacked: true,
        stackType: '100%',
      };
    }

    // Update x-axis categories
    if (data.value?.data?.headings) {
      freshOptions.xaxis = {
        ...freshOptions.xaxis,
        categories: data.value.data.headings,
      };
    }

    // Set width
    freshOptions.chart = {
      ...freshOptions.chart,
      width: chartWidth.value,
    };

    // Update the reactive ref with completely new object
    chartOptions.value = freshOptions;
  } catch (err) {
    console.error('formatData error:', err);
    status.value = 'error';
    return;
  }

  // Wait for next tick to ensure DOM is updated
  await nextTick();
  reRender();
  status.value = 'ready';
};

// --- fetching
const debouncedFetchData = debounce(async () => {
  await fetchData();
}, 600);

const fetchData = async () => {
  status.value = 'loading';

  try {
    data.value = await dashboardStore.fetchDataSource(props.widget.dataSourceGuid, {
      widgetGuid: props.widget.dashboardTabWidgetGuid,
    });
    emits('data-updated', data.value);
    await formatData();
  } catch (err) {
    console.error('fetchData error:', err);
    status.value = 'error';
  }
};
onMounted(fetchData);

defineExpose({ reRender, formatData, getSeriesInput });

// Watch for chart type changes - force complete re-render
watch(
  () => props.widget.defaultJsonProperties.currentDisplayType,
  async (newType, oldType) => {
    if (newType !== oldType && data.value) {
      // Don't fetch new data, just reformat with existing data
      status.value = 'formatting';
      await formatData();
    }
  },
  { immediate: true }
);

// Watch for parameter changes
watch(
  () => {
    const p = props.widget?.defaultJsonProperties?.parameters;
    return {
      results: p?.results ?? null,
      unit: p?.unit ?? null,
      benchmarkValues: p?.benchmarks?.values,
      filterByValues: p?.filterBy?.map((f) => f.value) ?? [],
      groupByValue: p?.groupBy?.value ?? null,
      orderByValue: p?.orderBy?.value ?? null,
      previousMonthsValue: p?.previousMonths?.value,
      previousRangeSelected: p?.previousRange?.selected ?? false,
      previousRangeFrom: p?.previousRange?.value[0] ?? '',
      previousRangeTo: p?.previousRange?.value[1] ?? '',
    };
  },
  async (newVal, oldVal) => {
    if (!oldVal) return;

    if (newVal.previousMonthsValue) {
      const min = props.widget?.defaultJsonProperties?.parameters?.previousMonths?.min ?? 1;
      const max = props.widget?.defaultJsonProperties?.parameters?.previousMonths?.max ?? null;

      if (newVal.previousMonthsValue < min) return;
      if (max && newVal.previousMonthsValue > max) return;
    }

    // Only fetch if values really changed
    if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
      await debouncedFetchData();
    }
  },
  { deep: false }
);

// Have to use a seperate one here, since its deep but using deep wont allow access to previous values.
watch(
  () => props.widget?.defaultJsonProperties?.parameters?.weights?.values,
  async (newVal) => {
    const sum = newVal?.reduce((total, val) => total + (Number(val) || 0), 0);
    if (sum !== 100) return;
    await debouncedFetchData();
  },
  { deep: true }
);
</script>

<template>
  <div class="tw-w-full ScrollBar ScrollBar--horizontal ScrollBar--vertical-hidden">
    <div class="tw-pb-3" :style="{ height: `${height}px` }">
      <vue-apex-charts
        v-if="shouldRenderChart"
        ref="chart"
        :type="chartOptions.chart?.type || 'bar'"
        :key="renderKey"
        :height="chartHeight"
        :width="chartWidth"
        :options="chartOptions"
        :series="seriesData.series"
      />
      <connx-dashboard-widget-no-data v-if="status === 'ready' && !hasData" />
      <component v-if="status === 'loading'" :is="skeletonMap.get(type!) ?? skeletonMap.get('default')" />
    </div>
  </div>
</template>
